// Wizard steps (spec §6, 05 §5.1, 90 #63). Pure helpers, no React.
//
// Route ids (lower-kebab, in the URL /exams/:examId/:step) map one-to-one to
// the wire ExamStep (UPPER_SNAKE, 01 §2.1). The client never decides whether
// a step may be entered: the stepper renders the server's `exam.steps[].state`
// (DONE | CURRENT | AVAILABLE | LOCKED) and a click asks the server
// (POST /exams/{id}/steps/{STEP}). These helpers only map, order and read.

export const ROUTE_STEPS = Object.freeze([
  'questions',
  'marking',
  'responses',
  'your-marks',
  'samples',
  'results',
]);

const ROUTE_TO_WIRE = Object.freeze({
  questions: 'QUESTIONS',
  marking: 'MARKING',
  responses: 'RESPONSES',
  'your-marks': 'YOUR_MARKS',
  samples: 'SAMPLES',
  results: 'RESULTS',
});

const WIRE_TO_ROUTE = Object.freeze(
  Object.fromEntries(
    Object.entries(ROUTE_TO_WIRE).map(([route, wire]) => [wire, route])
  )
);

// readyFor / stage values that land on a step's sub-view (01 §5.4).
const LANDING_TO_ROUTE = Object.freeze({
  ...WIRE_TO_ROUTE,
  CHECK_PAGES: 'responses',
});

export const STEP_LABELS = Object.freeze({
  questions: 'Questions',
  marking: 'Marking',
  responses: 'Responses',
  'your-marks': 'Your marks',
  samples: 'Samples',
  results: 'Results',
});

export const SETUP_STEP_COPY = {
  questions: {
    title: 'Add your exam questions',
    sub: 'Upload the question paper or type the questions in. JeddAI uses them to read and mark the responses.',
  },
  marking: {
    title: 'Marking',
    sub: 'Check how JeddAI will mark and give feedback. Anything you set in Questions is kept, and the rest is suggested for your task.',
  },
};

export const WIZARD_COPY = Object.freeze({
  kickerDouble: 'Exam marking · Double marking',
  kickerMarkForMe: 'Exam marking · Mark for me',
  kickerNone: 'Exam marking',
  untitled: 'New exam marking',
  backgroundJob:
    "Large sets can take a few minutes. You can leave this page and we'll email you when it's ready.",
});

const DOUBLE_STEPS = Object.freeze([
  'questions',
  'marking',
  'responses',
  'your-marks',
  'results',
]);
const MARK_FOR_ME_STEPS = Object.freeze([
  'questions',
  'marking',
  'responses',
  'samples',
  'results',
]);

/** Route ids for a path. A null path shows the No-path list (spec §6 [D]). */
export const stepsFor = (path) =>
  path === 'DOUBLE' ? DOUBLE_STEPS : MARK_FOR_ME_STEPS;

/** 'your-marks' → 'YOUR_MARKS'; unknown → null. */
export const toWire = (route) => ROUTE_TO_WIRE[route] || null;

/** 'YOUR_MARKS' → 'your-marks'; also CHECK_PAGES → 'responses'; unknown → null. */
export const toRoute = (wire) => LANDING_TO_ROUTE[wire] || null;

export const isRouteStep = (route) => ROUTE_STEPS.includes(route);

/** The step the exam resumes at (its server `step`), as a route id. */
export const resumeRoute = (exam) =>
  toRoute(exam?.step) || stepsFor(exam?.path)[0];

/**
 * The stepper rows: the server's `steps[]` when present, else the path's list
 * with Questions current and the rest locked (the unsaved /exams/new draft).
 * Each row: {route, wire, label, state, n}.
 */
export const stepperItems = (exam) => {
  const server = Array.isArray(exam?.steps) ? exam.steps : null;
  if (server && server.length > 0) {
    return server
      .filter((row) => toRoute(row.step))
      .map((row, index) => {
        const route = toRoute(row.step);
        return {
          route,
          wire: row.step,
          label: row.label || STEP_LABELS[route],
          state: row.state || 'LOCKED',
          n: index + 1,
        };
      });
  }
  return stepsFor(exam?.path).map((route, index) => ({
    route,
    wire: toWire(route),
    label: STEP_LABELS[route],
    state: index === 0 ? 'CURRENT' : 'LOCKED',
    n: index + 1,
  }));
};

/** The server state of one route step for this exam (LOCKED if absent). */
export const stepState = (route, exam) =>
  stepperItems(exam).find((item) => item.route === route)?.state || 'LOCKED';

/** A stepper row may be clicked unless the server says LOCKED. */
export const isClickable = (state) => state !== 'LOCKED';

/**
 * On Results (`locks.resultsOnly`) the earlier steps are locked, except
 * Samples on Mark for me, which can be revisited to recalibrate (spec §6).
 * The server still lists Marking as reachable there; the stepper doesn't
 * offer it.
 */
export const resultsLocked = (route, exam) =>
  Boolean(exam?.locks?.resultsOnly) &&
  route !== 'results' &&
  !(route === 'samples' && exam?.path === 'MARK_FOR_ME');

/**
 * The route step to render for a requested URL step. An unknown step, a step
 * not on this exam's path, or a LOCKED one resolves to the resume step.
 */
export const resolveStep = (requested, exam) => {
  const items = stepperItems(exam);
  const match = items.find((item) => item.route === requested);
  if (match && isClickable(match.state)) return requested;
  const resume = resumeRoute(exam);
  if (items.some((item) => item.route === resume)) return resume;
  return items[0]?.route || 'questions';
};

/** The next / previous route step on this exam's list (null at the ends). */
export const nextStep = (route, exam) => {
  const items = stepperItems(exam);
  const index = items.findIndex((item) => item.route === route);
  return index >= 0 && index < items.length - 1 ? items[index + 1].route : null;
};

export const previousStep = (route, exam) => {
  const items = stepperItems(exam);
  const index = items.findIndex((item) => item.route === route);
  return index > 0 ? items[index - 1].route : null;
};

/**
 * Read-only state of a step from `exam.locks` (01 §5.1):
 * - setupLocked / wordingOnly (marking started): Questions and Marking are
 *   read-only except the title, question text and stimulus text
 *   (`wordingEditable`).
 * - resultsOnly (on Results): every earlier step is read-only, except Samples
 *   on Mark for me (recalibration).
 * Returns {readOnly, wordingEditable}.
 */
export const isReadOnly = (route, exam) => {
  const locks = exam?.locks || {};
  const setup = Boolean(locks.setupLocked || locks.wordingOnly);
  const results = Boolean(locks.resultsOnly);
  if (route === 'results') return { readOnly: false, wordingEditable: false };
  if (route === 'samples') {
    const locked = results && exam?.path !== 'MARK_FOR_ME';
    return { readOnly: locked, wordingEditable: false };
  }
  if (route === 'questions' || route === 'marking') {
    return {
      readOnly: setup || results,
      wordingEditable: setup && !results,
    };
  }
  return { readOnly: results, wordingEditable: false };
};

/** Header kicker: the server's when given, else from the path. */
export const kickerFor = (exam) => {
  if (exam?.kicker) return exam.kicker;
  if (exam?.path === 'DOUBLE') return WIZARD_COPY.kickerDouble;
  if (exam?.path === 'MARK_FOR_ME') return WIZARD_COPY.kickerMarkForMe;
  return WIZARD_COPY.kickerNone;
};

/** Header title: the exam title, or "New exam marking". */
export const titleFor = (exam) =>
  (typeof exam?.title === 'string' && exam.title.trim()) ||
  WIZARD_COPY.untitled;

/** The wizard URL for a step (search kept by the caller when needed). */
export const stepPath = (examId, route) =>
  `/exams/${encodeURIComponent(examId)}/${route}`;
